import { useState } from 'react';
import { ExternalLink, FileText, Download } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Badge } from './ui/badge';

const fmtPence = (p, c = 'gbp') => new Intl.NumberFormat('en-GB', { style: 'currency', currency: (c || 'gbp').toUpperCase() }).format((p || 0) / 100);
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function AdopticReceiptButton({ id }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/billing/receipts/${id}/html`, { responseType: 'text' });
      const blob = new Blob([data], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `adoptic-receipt-${id}.html`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not fetch receipt'); } finally { setBusy(false); }
  };
  return <button onClick={open} disabled={busy} className="inline-flex items-center gap-1 text-brand-orange hover:underline font-body text-xs disabled:opacity-50" data-testid={`receipt-adoptic-${id}`}><Download className="w-3 h-3" /> Adoptic receipt</button>;
}

export function ReceiptsTable({ receipts, loading }) {
  if (loading) return <p className="p-6 font-body text-sm text-ink/50">Loading receipts…</p>;
  return (
    <div className="rounded-[6px] border border-ink/10 bg-white overflow-x-auto" data-testid="receipts-table">
      {receipts.length === 0 ? (
        <p className="p-6 font-body text-sm text-ink/50" data-testid="receipts-empty">No receipts yet — confirmed payments and paid invoices will appear here.</p>
      ) : (
        <table className="w-full min-w-[640px]">
          <thead className="bg-ink/[0.03] border-b border-ink/10">
            <tr>
              <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Date</th>
              <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Item</th>
              <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Total</th>
              <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Status</th>
              <th className="font-display text-xs font-extrabold text-ink p-3 text-right">Download</th>
            </tr>
          </thead>
          <tbody>
            {receipts.map(r => (
              <tr key={r.id} className="border-b border-ink/5 last:border-0 align-top" data-testid={`receipt-row-${r.kind}`}>
                <td className="p-3 font-body text-xs text-ink/60 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="p-3 font-body text-xs text-ink/80">{r.description}{r.number && <div className="text-ink/40">{r.number}</div>}{r.livemode === false && <Badge className="ml-1 bg-red-50 text-status-red border-red-200 font-body text-[10px]">TEST</Badge>}</td>
                <td className="p-3 font-body text-xs text-ink/80 whitespace-nowrap">{fmtPence(r.amount_pence, r.currency)}</td>
                <td className="p-3"><Badge className={`font-body text-[10px] capitalize ${r.status === 'paid' ? 'bg-forest-light text-forest border-forest/20' : 'bg-ink/5 text-ink/70 border-ink/10'}`}>{r.status}</Badge></td>
                <td className="p-3 text-right">
                  <div className="flex flex-col items-end gap-1">
                    {r.stripe_receipt_url && <a href={r.stripe_receipt_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-ink/70 hover:text-brand-orange font-body text-xs" data-testid={`receipt-stripe-${r.id}`}><ExternalLink className="w-3 h-3" /> {r.kind === 'invoice' ? 'Stripe invoice' : 'Stripe receipt'}</a>}
                    {r.pdf_url && <a href={r.pdf_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-ink/70 hover:text-brand-orange font-body text-xs" data-testid={`receipt-pdf-${r.id}`}><FileText className="w-3 h-3" /> PDF</a>}
                    {r.adoptic_receipt && <AdopticReceiptButton id={r.id} />}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
